import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { publicSearch } from '../../services/api';
import type { JunctionExitRoad } from '../../utils/junctionExitRoads';

export interface SearchableRoadMultiSelectProps {
  label: string;
  value: JunctionExitRoad[];
  onChange: (roads: JunctionExitRoad[]) => void;
  proximity?: { lat: number; lng: number } | null;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  inputClass: string;
  labelClass: string;
}

export function SearchableRoadMultiSelect({
  label,
  value,
  onChange,
  proximity = null,
  placeholder = 'Search road name…',
  hint,
  disabled,
  inputClass,
  labelClass,
}: SearchableRoadMultiSelectProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<JunctionExitRoad[]>([]);
  const [highlight, setHighlight] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const runFetch = useCallback(
    async (q: string) => {
      if (q.trim().length < 2) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const results = await publicSearch(
          q.trim(),
          8,
          'street',
          proximity ? { latitude: proximity.lat, longitude: proximity.lng } : undefined,
        );
        const roads = results
          .filter((result) => result.type === 'street' || result.type === 'address')
          .map((result) => ({
            locationId: result.id?.startsWith('result_') ? undefined : result.id,
            name: result.name.trim(),
          }))
          .filter((road) => road.name.length > 0);
        const seen = new Set<string>();
        const unique = roads.filter((road) => {
          const key = road.name.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setOptions(unique);
        setHighlight(0);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [proximity],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!open) return;
      void runFetch(query);
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, runFetch]);

  const addRoad = (road: JunctionExitRoad) => {
    const name = road.name.trim();
    if (!name) return;
    if (value.some((existing) => existing.name.toLowerCase() === name.toLowerCase())) return;
    onChange([...value, { locationId: road.locationId, name }]);
    setQuery('');
    setOpen(false);
    setOptions([]);
  };

  const removeRoad = (name: string) => {
    onChange(value.filter((road) => road.name !== name));
  };

  const pickHighlighted = () => {
    const option = options[highlight];
    if (option) addRoad(option);
  };

  return (
    <div ref={containerRef} className="space-y-2">
      <label className={labelClass}>{label}</label>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((road) => (
            <span
              key={road.name}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800 dark:bg-blue-950/50 dark:text-blue-200"
            >
              {road.name}
              <button
                type="button"
                onClick={() => removeRoad(road.name)}
                className="rounded-full p-0.5 hover:bg-blue-100 dark:hover:bg-blue-900/60"
                aria-label={`Remove ${road.name}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          type="text"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          className={inputClass}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurRef.current = setTimeout(() => setOpen(false), 150);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, Math.max(options.length - 1, 0)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              pickHighlighted();
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        {loading && (
          <Loader2
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"
          />
        )}
        {open && options.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-neutral-600 dark:bg-neutral-800">
            {options.map((option, index) => (
              <li key={`${option.name}-${index}`}>
                <button
                  type="button"
                  className={`w-full px-3 py-2 text-left text-sm ${
                    index === highlight
                      ? 'bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200'
                      : 'text-gray-800 dark:text-neutral-100'
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addRoad(option)}
                >
                  {option.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {hint && (
        <p className="text-xs text-gray-500 dark:text-neutral-500">{hint}</p>
      )}
    </div>
  );
}
